import type { BatchStatus } from '@beerwright/commands';
import { BATCH_STATUSES } from '@beerwright/commands';
import type { SearchSchemaInput } from '@tanstack/react-router';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { BatchList } from '@/components/batches/batch-list';
import { BATCH_SORTS, type BatchSort } from '@/lib/batch-search';
import { useReturnTicket } from '@/lib/return-ticket';
import { parseSearchEnum, parseSearchList, parseSearchString } from '@/lib/search-params';

/**
 * What `/batches` reads out of its own URL.
 *
 * The brew log is `/recipes` one surface over (PLAN-LIST-STATE D-LS10): a
 * brewer who narrows the log to what is fermenting, opens one to check a
 * gravity and presses Back should land on the fermenters, not on every batch
 * they have ever brewed. Same vocabulary as the recipe list, for the same
 * reason — one shape to learn, and a link that survives being pasted.
 */
export interface BatchesSearch {
  /** The search box. */
  readonly q: string | undefined;
  /** Active status chips, from a repeated `?status=` key. */
  readonly status: readonly BatchStatus[] | undefined;
  /** The sort control, or `undefined` for the default (`brewed-desc`). */
  readonly sort: BatchSort | undefined;
}

/**
 * The repeated `?status=` key, narrowed to statuses a batch can actually be
 * in.
 *
 * `parseSearchList` gets the shape right — one occurrence or several, trimmed
 * and de-duplicated — but it deals in strings, and this filter is compared
 * against a `BatchStatus` union. A hand-typed or stale value is dropped here
 * rather than carried through as a filter nothing can ever match: a URL
 * naming only nonsense is a URL with no status filter, not one asking for an
 * empty log (D-LS10).
 */
function parseStatuses(value: unknown): readonly BatchStatus[] | undefined {
  const listed = parseSearchList(value);
  if (listed === undefined) return undefined;
  const known = listed.filter((entry): entry is BatchStatus =>
    (BATCH_STATUSES as readonly string[]).includes(entry),
  );
  return known.length === 0 ? undefined : known;
}

export const Route = createFileRoute('/batches/')({
  validateSearch: (
    search: {
      readonly q?: unknown;
      readonly status?: unknown;
      readonly sort?: unknown;
    } & SearchSchemaInput,
  ): BatchesSearch => ({
    q: parseSearchString(search.q),
    status: parseStatuses(search.status),
    sort: parseSearchEnum(search.sort, BATCH_SORTS),
  }),
  component: BatchesPage,
});

function BatchesPage() {
  const { q, status, sort } = Route.useSearch();
  // Scoped `from`, not the bare hook: an unscoped `useNavigate` types `search`
  // against every route's schema at once, which cannot know this route's own
  // `q`/`status`/`sort` shape and rejects the updaters below.
  const navigate = useNavigate({ from: Route.fullPath });
  // The view is in the URL; this is what lets the batch page come back to it.
  // Opening a batch stamps the entry it pushes with this log's own position,
  // so "Batches" returns to the log the brewer narrowed rather than pushing a
  // bare `/batches` over the top of it (PLAN-LIST-STATE D-LS7).
  const returnTicket = useReturnTicket();
  return (
    <BatchList
      // The search *is* the view: `BatchList` reads these on every render
      // rather than seeding from them, so a Back that only changes the search
      // params — which does not remount this route — moves the log with it.
      query={q}
      statusFilter={status}
      sort={sort}
      // `replace: true`: a keystroke narrows the same search, it does not
      // start a new one, so Back should undo the whole search box rather than
      // walk back through "p", "pa", "pal" one letter at a time.
      onQueryChange={(next) => {
        void navigate({
          search: (prev) => ({ ...prev, q: next === '' ? undefined : next }),
          replace: true,
        });
      }}
      // Choosing a status is a decision, not a keystroke: it pushes, so Back
      // undoes one chip at a time instead of jumping past the search that led
      // to it.
      onStatusFilterChange={(next) => {
        void navigate({
          search: (prev) => ({ ...prev, status: next.length === 0 ? undefined : next }),
        });
      }}
      // `brewed-desc` is the log's default and never reaches the URL (D-LS2):
      // one view spelled two ways is a view Back can land on twice.
      onSortChange={(next) => {
        void navigate({
          search: (prev) => ({ ...prev, sort: next === 'brewed-desc' ? undefined : next }),
        });
      }}
      // "Clear" empties the search box and the chips together, so it is one
      // navigation and one history entry. Two — a replace for the box then a
      // push for the chips — would leave Back on a half-cleared log nobody
      // was ever shown.
      onClearFilters={() => {
        void navigate({
          search: (prev) => ({ ...prev, q: undefined, status: undefined }),
        });
      }}
      onOpen={(batchId) => {
        void navigate({ to: '/batches/$batchId', params: { batchId }, state: returnTicket });
      }}
      onOpenRecipes={() => {
        void navigate({ to: '/recipes' });
      }}
      // Straight to the sheet, with the ticked ids in the URL and no question
      // asked first: the sheet answers every refusal itself — signed out, the
      // wrong plan, a run past the cap, a mint that failed — which is the same
      // bargain the Feedback page's "Print a card" makes for one beer.
      onPrintCards={(ids) => {
        void navigate({ to: '/batches/tasting-cards', search: { batches: ids.join(',') } });
      }}
    />
  );
}
